import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cva, type VariantProps } from "class-variance-authority";
import ModalFooter from "../ModalFooter";
import { BaseModalProps } from "@/types";
import CloseModal from "@/icons/close/closeModal.svg";
import Image from "next/image";
import { useId } from "react";

const paddedModalVariants = cva("px-6 pt-4 pb-2 !w-[29.625rem] !rounded-[16px] !max-h-[99%]", {
  variants: {
    variant: {
      default: "",
      loading: "flex items-center justify-center !w-[36rem] !max-w-xl",
      noOverlay: "py-0 !w-[21.75rem] rounded-[16px] border border-secondary-light shadow-custom-card-shadow",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const noPaddingModalVariants = cva("!w-[29.625rem] !max-h-[99%] !rounded-[16px]", {
  variants: {
    variant: {
      default: "",
      loading: "flex items-center justify-center !w-[36rem] !max-w-xl",
      noOverlay: "py-0 !w-[21.75rem] rounded-[16px] border border-secondary-light shadow-custom-card-shadow",
    },
  },
  defaultVariants: { variant: "default" },
});

export interface ModalProps extends BaseModalProps, VariantProps<typeof paddedModalVariants> {
  hideDialogDescription?: boolean;
  isRaiModal?: boolean;
  isModalRAISuccess?: boolean;
  overlayClassName?: string;
  modalContentClassName?: string;
  showClose?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  onConfirm,
  onButtonClick,
  modalTitle,
  modalDescription,
  variant = "default",
  leftButtonText = "No, return",
  rightButtonText = "Yes, cancel",
  children,
  descriptionClassName = "",
  contentClassName = "",
  leftButtonVariant,
  rightButtonVariant,
  hideDialogueTitle = false,
  dataAttributes,
  leftButtonEventClass,
  rightButtonEventClass,
  hideDialogDescription,
  removeOverflow = false,
  isRaiModal = false,
  overlayClassName = "",
  modalContentClassName = "",
  showClose = true,
}: Readonly<ModalProps>) {
  const titleId = useId();
  const descriptionId = useId();

  const modalVariants = showClose ? noPaddingModalVariants : paddedModalVariants;

  let showCloseClasses = "";
  if (showClose) {
    showCloseClasses = "bg-transparent p-0 border-0";
  } else if (!removeOverflow) {
    showCloseClasses = "overflow-auto";
  }

  let innerContainerClasses = "";
  if (showClose) {
    const overflowClass = removeOverflow ? "overflow-visible" : "overflow-auto";
    innerContainerClasses = `${overflowClass} max-h-[min(80vh,calc(100dvh-6rem))] p-6 ${modalContentClassName}`;
  }

  return (
    <Dialog
      data-testid="modal"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose?.();
        }
      }}
    >
      <DialogContent
        aria-describedby={hideDialogDescription ? undefined : descriptionId}
        aria-labelledby={titleId}
        className={`${modalVariants({ variant })} ${contentClassName} ${showCloseClasses}`}
        isOverlay={variant !== "noOverlay"}
        overlayClassName={overlayClassName}
        aria-modal="true"
        {...(isRaiModal && { onInteractOutside: (e) => e.preventDefault() })}
      >
        {/* Minimal padding to avoid viewport clipping for showing close button */}
        <div className={`${showClose ? "relative pt-4 pr-4" : ""}`}>
          {/* The card is the positioning anchor for the pill */}
          <div className={`${showClose ? "relative overflow-visible bg-white rounded-2xl shadow-md" : ""}`}>
            {/* Pixel-perfect Close pill; positioned relative to the card */}
            {showClose && (
              <button
                type="button"
                onClick={() => {
                  onClose?.("Close");
                  onButtonClick?.("Close");
                }}
                aria-label="Close modal"
                data-testid="modal-close"
                className={`absolute top-0 right-0 translate-y-[-72%] inline-flex items-center justify-center p-0 m-0 bg-transparent border-0 w-auto h-auto`}
              >
                <Image
                  src={CloseModal}
                  alt=""
                  aria-hidden="true"
                  width={92}
                  height={40}
                  className="pointer-events-none select-none block"
                />
              </button>
            )}

            {/* Scrollable content inside the card */}
            <div className={innerContainerClasses}>
              {hideDialogueTitle && <DialogTitle id={titleId} />}
              {children ? (
                <>{children}</>
              ) : (
                <>
                  <DialogHeader className="mb-5">
                    <DialogTitle
                      id={titleId}
                      className="mb-[10px] text-mondelez-primary-purple font-epilogue text-[22px] font-bold leading-[30px] tracking-[-0.44px]"
                    >
                      {modalTitle}
                    </DialogTitle>
                    {!hideDialogDescription && (
                      <DialogDescription
                        id={descriptionId}
                        className={`text-mondelez-primary-purple text-base font-normal leading-[137.5%] tracking-[-0.32px] w-[90%] ${descriptionClassName}`}
                      >
                        {modalDescription}
                      </DialogDescription>
                    )}
                  </DialogHeader>
                  <ModalFooter
                    leftButtonVariant={leftButtonVariant}
                    rightButtonVariant={rightButtonVariant}
                    onClose={onClose}
                    onConfirm={onConfirm}
                    onButtonClick={onButtonClick}
                    leftButtonText={leftButtonText}
                    rightButtonText={rightButtonText}
                    dataAttributes={dataAttributes}
                    leftButtonEventClass={leftButtonEventClass}
                    rightButtonEventClass={rightButtonEventClass}
                    isRaiModal={isRaiModal}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
